.chart {
  margin: 0;
  padding: 12px 0;
  background: var(--surface);
  border: var(--outline);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
}

@media (max-width: 719px) {
  .chart {
    display: none;
  }
}

/* A long month is wider than the column on smaller desktops; the table
 * scrolls inside the card rather than widening the page. The card's left gap
 * belongs to the pinned cells rather than to the scroll box, so what
 * scrolls under them never shows beside them (#92). */
.scroll {
  overflow-x: auto;
  padding: 0 16px 0 0;
}

/* Separate borders, so every line belongs to its own cell: a pinned label
 * carries its row line and its rule with it, and the days' lines scroll
 * under it rather than across it (#92). */
.table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}

.caption {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.corner,
.label {
  position: sticky;
  left: 0;
  z-index: 1;
  padding-left: 16px;
  border-right: 1.5px solid var(--ink);
  background: var(--surface);
}

.label {
  min-width: 132px;
  padding: 0 10px 0 16px;
  text-align: left;
  font-weight: 500;
  color: var(--ink-2);
  white-space: nowrap;
}

.values .label {
  font-weight: 700;
  color: var(--ink);
}

.row {
  height: 30px;
}

.row > * {
  border-top: 1px dashed var(--line);
}

/* Normal is the sheet's middle line, and the first value row starts the lower
 * half: both get a solid rule. */
.normal > *,
.normal + .row > *,
.values > * {
  border-top: 1.5px solid var(--ink);
}

/* 26px a day: a 31-day month and its labels, Italian's longer mood names
 * included, fit a desktop column without hiding any name (#92). */
.cell {
  width: 26px;
  min-width: 26px;
  border-left: 1px solid var(--line);
  text-align: center;
  font-weight: 600;
}

/* A day that can be chosen takes the pointer over its whole column, and the
 * column it is pointed or focused in is marked (#146). The selected day keeps
 * its own colour under the pointer; the marks and values keep theirs. */
.available {
  cursor: pointer;
}

.marked {
  background: var(--sunk);
}

.selected {
  background: var(--accent-soft);
}

.future {
  color: var(--ink-3);
  opacity: 0.6;
}

.day {
  width: 100%;
  min-height: 28px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.day:disabled {
  cursor: default;
}

.day:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: -2px;
  border-radius: 4px;
}

.mark {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 1.5px solid var(--ink);
  border-radius: 4px;
  background: var(--swatch);
  vertical-align: middle;
}

.key {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 8px;
  border: 1.5px solid var(--ink);
  border-radius: 2px;
  background: var(--swatch);
}

.note {
  padding: 10px 16px 0;
  font-size: 13px;
  color: var(--ink-3);
}

/* Both charts as the blueprint's Patient diary draws them (#89, #92, #146):
 * the pinned column is ruled off from the days, and the measures carry
 * icons. */
.icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  margin-right: 8px;
  vertical-align: middle;
  background-color: currentColor;
  -webkit-mask: var(--icon) no-repeat center / contain;
  mask: var(--icon) no-repeat center / contain;
}

.anxiety {
  --icon: url('./anxiety.svg');
}

.irritability {
  --icon: url('./irritability.svg');
}

.sleep {
  --icon: url('./sleep.svg');
}

.weight {
  --icon: url('./weight.svg');
}

/* On phones too, as a clinician reads a patient's month: the chart stays,
 * scrolling sideways under a narrow column of swatches and icons; the names
 * leave the screen for the Legend but stay in the row headers. */
@media (max-width: 719px) {
  .onPhones {
    display: block;
  }

  .onPhones .label {
    min-width: 0;
    padding-right: 8px;
  }

  .onPhones .label .key,
  .onPhones .label .icon {
    margin-right: 0;
  }

  .onPhones .label .key {
    width: 14px;
    height: 14px;
  }

  .onPhones .name {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
}

.legend {
  margin: 10px 16px 0;
  padding-top: 4px;
  border-top: 1.5px solid var(--ink);
}

@media (min-width: 720px) {
  .legend {
    display: none;
  }
}

.summary {
  display: flex;
  align-items: center;
  min-height: 44px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.legendColumns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}

.legendList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0 0 4px;
  list-style: none;
  font-size: 13px;
  color: var(--ink-2);
}

.legendItem {
  display: flex;
  align-items: center;
}

.legendItem .key,
.legendItem .icon {
  flex: 0 0 auto;
}

/* A day with nothing recorded is an empty column. */
.empty {
  border-style: dashed;
  border-color: var(--ink-3);
  background: none;
}
